/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useAuth } from '../../../authContext';
import { getOverview } from '../services/api';
import OperatorBoard from '../components/OperatorBoard';
import styles from '../styles/Dashboard.module.css';

const PROJECT_OPTIONS = [
    { label: 'Ambos', value: 'ambos' },
    { label: 'Relojes', value: 'watch' },
    { label: 'Tablets', value: 'tablet' },
];

export default function SubscriptionInsightsDashboard() {
    const { tokens } = useAuth();
    const [messageApi, contextHolder] = message.useMessage();
    const [project, setProject] = useState('ambos');
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const latestRequest = useRef(0);

    const load = useCallback(
        async (refresh = false) => {
            if (!tokens?.AccessToken) return;
            const requestId = latestRequest.current + 1;
            latestRequest.current = requestId;
            setLoading(true);
            try {
                const projects = project === 'ambos' ? ['watch', 'tablet'] : [project];
                const overviews = [];
                // Relojes y tablets de a uno: el backend comparte un único
                // cliente de Parse y lo apunta a una app u otra en cada request.
                // Pedirlos a la vez arriesga que una lectura caiga en la base
                // equivocada.
                for (const key of projects) {
                    // eslint-disable-next-line no-await-in-loop
                    overviews.push(await getOverview(tokens.AccessToken, key, { refresh }));
                }
                // Si el agente cambió de filtro mientras cargaba, esta respuesta
                // ya no es la que está mirando.
                if (latestRequest.current !== requestId) return;
                setOverview({ projects: overviews });
            } catch (error) {
                if (latestRequest.current !== requestId) return;
                messageApi.error(
                    error.response?.data?.message ||
                        'No se pudo cargar la información de suscripciones'
                );
            } finally {
                if (latestRequest.current === requestId) setLoading(false);
            }
        },
        [tokens?.AccessToken, project, messageApi]
    );

    useEffect(() => {
        load(false);
    }, [load]);

    const overviewByProject = Object.fromEntries(
        (overview?.projects || []).map((item) => [item.project, item])
    );
    const visibleProjects = project === 'ambos' ? ['watch', 'tablet'] : [project];

    return (
        <div className={styles.page}>
            {contextHolder}
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>Suscripciones</h1>
                    <p className={styles.subtitle}>
                        Parque por operador: cuántas hay, en qué estado y quién cobra.
                    </p>
                </div>
                <button
                    type="button"
                    className={styles.primaryBtn}
                    disabled={loading}
                    onClick={() => load(true)}
                >
                    Actualizar
                </button>
            </div>

            <div className={styles.filters} role="tablist" aria-label="Producto">
                {PROJECT_OPTIONS.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        role="tab"
                        aria-selected={project === option.value}
                        className={
                            project === option.value
                                ? styles.filterActive
                                : styles.filterBtn
                        }
                        onClick={() => setProject(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            {visibleProjects.map((key) => (
                <OperatorBoard
                    key={key}
                    project={key}
                    projectData={overviewByProject[key]}
                    loading={loading}
                />
            ))}
        </div>
    );
}
